import type {
  IneligibleReason,
  PayRunInput,
  PayRunLine,
  PayRunStatus,
  PayRunType,
  PayrollAttendanceCounts,
  RunLineFlag,
  SpecialRunComponent,
} from '@thrivehrm/shared';
import { BLOCKING_LINE_FLAGS, ZERO_SALARY_EXCLUDE_REASON } from '@thrivehrm/shared';
import type { StepperStep } from '@/components/ui/stepper';

/** Run types HR can create on `/payroll/runs/new`. */
export const NEW_RUN_TYPES = ['MONTHLY', 'BONUS', 'LTA', 'SETTLEMENT'] as const;

export const RUN_TYPE_LABELS: Readonly<Record<PayRunType, string>> = {
  MONTHLY: 'Monthly',
  BONUS: 'Bonus',
  LTA: 'LTA',
  SETTLEMENT: 'Settlement',
};

/** Check flags (FR-PAY-16, ADR-064): label and what to do. */
export const FLAG_INFO: Readonly<Record<RunLineFlag, { label: string; hint: string }>> = {
  NEGATIVE_NET: {
    label: 'Negative net',
    hint: 'Deductions exceed earnings. Reduce a deduction before submitting.',
  },
  ZERO_NET: {
    label: 'Zero net',
    hint: 'Salary is 0 — fix it or it will be left out. Check LWP days and deductions.',
  },
  NO_BANK_ACCOUNT: {
    label: 'No bank account',
    hint: 'Part of the pay goes to the bank but no account number is on file.',
  },
  NO_SALARY: {
    label: 'No salary',
    hint: 'Salary is 0 — fix it or it will be left out. No salary is recorded for the first employed day.',
  },
  DEDUCTION_SPILLOVER: {
    label: 'Deduction spill-over',
    hint: 'A deduction did not fit in its usual part and was taken from the other part.',
  },
  NEW_JOINER: { label: 'New joiner', hint: 'Joined this month; pay is prorated.' },
  LWP_CAPPED: {
    label: 'LWP capped',
    hint: 'LWP days were more than the days employed and were reduced.',
  },
  AMOUNT_OVERRIDDEN: {
    label: 'Amount overridden',
    hint: 'HR replaced the calculated amount; the reason is in the breakdown.',
  },
  NO_EXIT: {
    label: 'No exit',
    hint: 'The exit was removed after this settlement was made. Cancel the run or settle someone else.',
  },
  IN_MONTHLY_RUN: {
    label: 'In monthly run',
    hint: "The month's monthly run still pays this person. Recalculate it so they are paid only here.",
  },
  PREVIOUS_MONTH_UNPAID: {
    label: 'Previous month unpaid',
    hint: 'No approved monthly run paid this person for the month before. This run pays only the last month.',
  },
};

/** Components a special run pays (FR-PAY-13). */
export const SPECIAL_COMPONENT_LABELS: Readonly<Record<SpecialRunComponent, string>> = {
  FESTIVAL_BONUS: 'Festival bonus',
  PROFIT_BONUS: 'Profit bonus',
  BONUS: 'Bonus',
  LTA: 'LTA',
};

/** Why people are left out of a special run, per first failing criterion. */
export const INELIGIBLE_LABELS: Readonly<Record<IneligibleReason, string>> = {
  STATUS: 'Not active or on notice on the cutoff',
  EMPLOYMENT_TYPE: 'Other employment type',
  JOINED_AFTER: 'Joined after the limit',
  NOT_CONFIRMED: 'Not confirmed by the limit',
};

export const isBlocking = (flag: string): boolean =>
  (BLOCKING_LINE_FLAGS as readonly string[]).includes(flag);

/** A line left out on submission because its salary was 0 (FR-PAY-23, ADR-088). */
export const isZeroSalaryExclusion = (line: Pick<PayRunLine, 'excluded' | 'excludeReason'>) =>
  line.excluded && line.excludeReason === ZERO_SALARY_EXCLUDE_REASON;

export const flagLabel = (flag: string): string =>
  flag in FLAG_INFO ? FLAG_INFO[flag as RunLineFlag].label : flag;

/** Inputs editable in a monthly run grid (FR-PAY-2…4), in column order. */
export const GRID_INPUTS = [
  'LWP_DAYS',
  'ADVANCE',
  'FOOD',
  'AIT',
  'PF',
  'BONUS',
  'LTA',
  'ARREAR',
] as const satisfies readonly PayRunInput[];
export type GridInput = (typeof GRID_INPUTS)[number];

export const INPUT_LABELS: Readonly<Record<GridInput, string>> = {
  LWP_DAYS: 'LWP days',
  ADVANCE: 'Advance',
  FOOD: 'Food',
  AIT: 'AIT',
  PF: 'PF',
  BONUS: 'Bonus',
  LTA: 'LTA',
  ARREAR: 'Arrear',
};

export const SOURCE_LABELS: Readonly<Record<string, string>> = {
  SALARY: 'Salary',
  STANDING: 'Standing',
  MANUAL: 'Entered',
  PROVIDER: 'From attendance & leave',
  COMPUTED: 'Calculated',
};

/** Attendance counts beside each line (FR-PAY-20, ADR-088), in display order. */
export const ATTENDANCE_COUNTS = [
  { key: 'absent', total: 'absentDays', label: 'Absent' },
  { key: 'late', total: 'lateDays', label: 'Late' },
  { key: 'unpaidLeave', total: 'unpaidLeaveDays', label: 'Unpaid leave' },
] as const satisfies readonly {
  key: keyof PayrollAttendanceCounts['dates'];
  total: Exclude<keyof PayrollAttendanceCounts, 'dates'>;
  label: string;
}[];

/** A line's value for one input (0 when none is stored). */
export const inputValue = (line: PayRunLine, input: GridInput): number =>
  line.inputs.find((i) => i.input === input)?.value ?? 0;

/** HR's decision on a bonus / LTA line (FR-PAY-13). */
export type LineDecision = 'CALCULATED' | 'OVERRIDE' | 'EXCLUDE';

/** The line's current decision, from its exclusion and `SPECIAL_AMOUNT` override. */
export function lineDecision(line: PayRunLine): {
  decision: LineDecision;
  amount: number | null;
  reason: string;
} {
  const override = line.inputs.find((i) => i.input === 'SPECIAL_AMOUNT');
  if (line.excluded) return { decision: 'EXCLUDE', amount: null, reason: line.excludeReason ?? '' };
  if (override) {
    return { decision: 'OVERRIDE', amount: override.value, reason: override.reason ?? '' };
  }
  return { decision: 'CALCULATED', amount: null, reason: '' };
}

/** The run workflow (FR-PAY-12, ADR-052) as stepper steps; Cancelled is not a step. */
const RUN_STEPS: readonly { status: PayRunStatus; label: string }[] = [
  { status: 'DRAFT', label: 'Draft' },
  { status: 'SUBMITTED', label: 'Submitted for approval' },
  { status: 'APPROVED', label: 'Approved' },
  { status: 'DISBURSED', label: 'Disbursed' },
];

/**
 * Stepper steps for a run status: the steps before it done, its own step current, the rest
 * upcoming; a disbursed run has every step done. A cancelled run has no steps (empty list).
 */
export function runSteps(status: PayRunStatus): StepperStep[] {
  const current = RUN_STEPS.findIndex((s) => s.status === status);
  if (current < 0) return [];
  return RUN_STEPS.map((s, i) => ({
    id: s.status,
    label: s.label,
    state: i < current || status === 'DISBURSED' ? 'done' : i === current ? 'current' : 'upcoming',
  }));
}
